import { Resources } from 'constants/resources';

import { hydraSchemaAnalyzer } from '@api-platform/admin';
import {
  Form,
  HttpError,
  RecordContextProvider,
  required,
  useNotify,
  useUpdate,
} from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { Button, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { NumberInput, TextInput } from 'shared/react-admin/Inputs';
import { MonitoringSetting } from 'types/embedded/monitoringSetting';
import { getServerErrorMessage } from 'utils/getServerErrorMessage';

type Props = {
  open: boolean;
  onClose: () => void;
  record: MonitoringSetting | undefined;
};

export const MonitoringSettingsEditDialog: React.FC<Props> = ({ open, onClose, record }) => {
  const [update] = useUpdate();
  const notify = useNotify();

  const onSubmit = async ({ alias, settings }: FieldValues) => {
    try {
      await update(
        Resources.MonitoringSettings,
        { id: record?.id, data: { alias, settings } },
        { returnPromise: true },
      );
      notify('Настройки мониторинга успешно отредактированы!', { type: 'success' });
      onClose();
    } catch (error) {
      const submissionErrors = hydraSchemaAnalyzer().getSubmissionErrors(error as HttpError);
      if (submissionErrors) {
        return submissionErrors;
      }
      notify(`Ошибка: ${await getServerErrorMessage(error)}`, { type: 'error' });
    }
  };

  return (
    <Dialog fullWidth onClose={onClose} open={open}>
      <RecordContextProvider value={record}>
        <Form onSubmit={onSubmit}>
          <CardHeader
            appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
            title="Редактирование настроек мониторинга"
            titleSize="smallBold"
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: '16px',
              padding: '24px 24px 0',
            }}
          >
            <TextInput
              fullWidth
              label="ID"
              readOnly
              size="small"
              source="originId"
              variant="outlined"
            />
            <TextInput
              fullWidth
              label="Алиас"
              size="small"
              source="alias"
              validate={required()}
              variant="outlined"
            />
            <NumberInput
              fullWidth
              label="Настройки"
              size="small"
              source="settings.alertMinCount"
              validate={required()}
              variant="outlined"
            />
          </Box>
          <CardActionsButtons
            rightActionsSlot={[
              <Button key="cancel" label="Отменить" onClick={onClose} variant="outlined" />,
              <SaveButton key="submit" label="Сохранить" />,
            ]}
          />
        </Form>
      </RecordContextProvider>
    </Dialog>
  );
};
